import React from "react";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import styles from "./sponsor.module.css";

interface Sponsor {
  name: string;
  type: string;
  logo?: { url?: string };
}

interface SponsorSectionProps {
  sponsors: Sponsor[];
  primaryColor: string;
  secondaryColor: string;
}

// sponsor/co-sponsor section
const responsiveSponsors = {
  desktop: { breakpoint: { max: 3000, min: 1024 }, items: 3 },
  tablet: { breakpoint: { max: 1024, min: 464 }, items: 2 },
  mobile: { breakpoint: { max: 464, min: 0 }, items: 1 },
};

// powered/co-powered section
const responsivePowered = {
  desktop: { breakpoint: { max: 3000, min: 1024 }, items: 3 },
  tablet: { breakpoint: { max: 1024, min: 464 }, items: 2 },
  mobile: { breakpoint: { max: 464, min: 0 }, items: 1 },
};

// associate sponsor section
const responsiveAssociate = {
  desktop: { breakpoint: { max: 3000, min: 1024 }, items: 3 },
  tablet: { breakpoint: { max: 1024, min: 464 }, items: 3 },
  mobile: { breakpoint: { max: 464, min: 0 }, items: 1 },
};

// streaming partner section
const responsiveStreaming = {
  desktop: { breakpoint: { max: 3000, min: 1024 }, items: 3 },
  tablet: { breakpoint: { max: 1024, min: 464 }, items: 3 },
  mobile: { breakpoint: { max: 464, min: 0 }, items: 1 },
};

const CustomDot = ({ onClick, ...rest }: any) => {
  const { active } = rest;
  return (
    <button
      className={`${styles.diamondDot} ${active ? styles.diamondDotActive : ""}`}
      onClick={() => onClick()}
    ></button>
  );
};

const SponsorItem = ({ sponsor }: { sponsor: Sponsor }) => {
  return (
    <div className={styles.sponsorItemWrapper}>
      <div
        className={styles.sponsorItem}
        style={{ backgroundImage: `url(${sponsor?.logo?.url})` }}
      />
      <p className={styles.sponsorName}>{sponsor.name}</p>
    </div>
  );
};

const SponsorSection: React.FC<SponsorSectionProps> = ({
  sponsors,
  primaryColor,
  secondaryColor,
}) => {
  const sponsored = sponsors?.filter(s => s.type?.toLowerCase() === "sponsored by") || [];
  const coSponsored = sponsors?.filter(s => s.type?.toLowerCase() === "co-sponsored by") || [];
  const sponsorList = [...sponsored, ...coSponsored];

  const powered = sponsors?.filter(s => s.type?.toLowerCase() === "powered by") || [];
  const coPowered = sponsors?.filter(s => s.type?.toLowerCase() === "co-powered by") || [];
  const poweredList = [...powered, ...coPowered];

  const associateList = sponsors?.filter(s => s.type?.toLowerCase() === "associate sponsor") || [];
  const streamingList = sponsors?.filter(s => s.type?.toLowerCase() === "streaming partner") || [];

  if (!sponsors || sponsors.length === 0) {
    return null;
  }

  const hasAnySponsor =
    sponsorList.length > 0 ||
    poweredList.length > 0 ||
    associateList.length > 0 ||
    streamingList.length > 0;

  if (!hasAnySponsor) return null;

  return (
    <div className={styles.sponsorsSection}>
      <h2 className="fs-24 fw-bold text-center mb-4" style={{ color: primaryColor }}>
        OUR SPONSORS
      </h2>

      {/* SPONSOR & CO-SPONSOR SECTION */}
      {sponsorList.length > 0 && (
        <div className={`${styles.sponsorCategory} ${styles.largeSponsorSection}`}>
          <h2 className="fs-16 text-center" style={{ color: primaryColor }}>
            {sponsored.length > 0 && coSponsored.length > 0
              ? "SPONSORED AND CO-SPONSORED BY"
              : sponsored.length > 0
                ? "SPONSORED BY"
                : "CO-SPONSORED BY"}
          </h2>
          {sponsorList.length > 3 ? (
            <Carousel
              responsive={responsiveSponsors}
              infinite={true}
              autoPlay={true}
              autoPlaySpeed={1500}
              customDot={<CustomDot />}
              showDots={true}
              arrows={false}
              itemClass={styles.carouselItem}
            >
              {sponsorList.map((sponsor, i) => (
                <SponsorItem key={i} sponsor={sponsor} />
              ))}
            </Carousel>
          ) : (
            <div
              style={{
                display: "flex",
                gap: "18px",
                flexWrap: "wrap",
                justifyContent: "center",
                width: "100%",
              }}
            >
              {sponsorList.map((sponsor, i) => (
                <SponsorItem key={i} sponsor={sponsor} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* POWERED & CO-POWERED SECTION */}
      {poweredList.length > 0 && (
        <div className={styles.sponsorCategory}>
          <h2 className="fs-16 text-center" style={{ color: primaryColor }}>
            {powered.length > 0 && coPowered.length > 0
              ? "POWERED AND CO-POWERED BY"
              : powered.length > 0
                ? "POWERED BY"
                : "CO-POWERED BY"}
          </h2>
          {poweredList.length > 2 ? (
            <Carousel
              responsive={responsivePowered}
              infinite={true}
              autoPlay={true}
              autoPlaySpeed={1500}
              customDot={<CustomDot />}
              showDots={true}
              arrows={false}
              itemClass={styles.carouselItem}
            >
              {poweredList.map((sponsor, i) => (
                <SponsorItem key={i} sponsor={sponsor} />
              ))}
            </Carousel>
          ) : (
            <div
              style={{
                display: "flex",
                gap: "18px",
                flexWrap: "wrap",
                justifyContent: "center",
                width: "100%",
              }}
            >
              {poweredList.map((sponsor, i) => (
                <SponsorItem key={i} sponsor={sponsor} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ASSOCIATE & STREAMING SECTION */}
      {(associateList.length > 0 || streamingList.length > 0) && (
        <div className={styles.associateStreamingRow}>
          {associateList.length > 0 && (
            <div>
              <div className={styles.sponsorCategory}>
                <h2 className="fs-16 text-center" style={{ color: primaryColor }}>
                  ASSOCIATE SPONSOR
                </h2>
                {associateList.length > 2 ? (
                  <Carousel
                    responsive={responsiveAssociate}
                    infinite={true}
                    autoPlay={true}
                    autoPlaySpeed={2000}
                    customDot={<CustomDot />}
                    showDots={true}
                    arrows={false}
                    itemClass={styles.carouselItem}
                  >
                    {associateList.map((sponsor, i) => (
                      <SponsorItem key={i} sponsor={sponsor} />
                    ))}
                  </Carousel>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      gap: "18px",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      width: "100%",
                    }}
                  >
                    {associateList.map((sponsor, i) => (
                      <SponsorItem key={i} sponsor={sponsor} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          {streamingList.length > 0 && (
            <div>
              <div className={styles.sponsorCategory}>
                <h2 className="fs-16 text-center" style={{ color: primaryColor }}>
                  STREAMING PARTNER
                </h2>
                {streamingList.length > 2 ? (
                  <Carousel
                    responsive={responsiveStreaming}
                    infinite={true}
                    autoPlay={true}
                    autoPlaySpeed={2500}
                    customDot={<CustomDot />}
                    showDots={true}
                    arrows={false}
                    itemClass={styles.carouselItem}
                  >
                    {streamingList.map((sponsor, i) => (
                      <SponsorItem key={i} sponsor={sponsor} />
                    ))}
                  </Carousel>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      gap: "18px",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      width: "100%",
                    }}
                  >
                    {streamingList.map((sponsor, i) => (
                      <SponsorItem key={i} sponsor={sponsor} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SponsorSection;
